import { useState, useCallback, useMemo } from 'react'
import { Check, ChevronsUpDown, Loader2, Plane, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { useAircraftTypes } from '@/hooks/useAircraftTypes'
import {
  AIRCRAFT_CATEGORY_LABELS,
  type AircraftCategory,
  type AircraftTypeRow,
} from '@/types/aircraft-types'

interface AircraftTypeAutocompleteProps {
  /** Callback when an aircraft type is selected */
  onSelect: (aircraftType: AircraftTypeRow) => void
  /** Currently selected aircraft type ID (for controlled mode) */
  value?: string | null
  /** Disable the autocomplete */
  disabled?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Custom class name */
  className?: string
}

/**
 * Aircraft type autocomplete component
 * Searches the internal aircraft_types table
 */
export function AircraftTypeAutocomplete({
  onSelect,
  value,
  disabled = false,
  placeholder = 'Search aircraft types...',
  className,
}: AircraftTypeAutocompleteProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch aircraft types with search filter
  const { data: aircraftTypes, isLoading } = useAircraftTypes({
    search: searchQuery.length >= 2 ? searchQuery : undefined,
    isActive: true,
  })

  // Find currently selected type for display
  const { data: allTypes } = useAircraftTypes({ isActive: true })
  const selectedType = useMemo(() => {
    if (!value || !allTypes) return null
    return allTypes.find((t) => t.id === value) || null
  }, [value, allTypes])

  const handleSelect = useCallback(
    (aircraftType: AircraftTypeRow) => {
      onSelect(aircraftType)
      setOpen(false)
      setSearchQuery('')
    },
    [onSelect]
  )

  const getCategoryLabel = (category: string | null | undefined): string => {
    if (!category) return ''
    return AIRCRAFT_CATEGORY_LABELS[category as AircraftCategory] || category
  }

  const displayValue = selectedType
    ? `${selectedType.manufacturer} ${selectedType.model}${selectedType.variant ? ` ${selectedType.variant}` : ''}`
    : ''

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selectedType && 'text-muted-foreground',
            className
          )}
        >
          <div className="flex items-center gap-2 truncate">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <span className="truncate">{displayValue || placeholder}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type to search (e.g., Citation CJ3, Gulfstream G650)..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching...
              </div>
            )}

            {!isLoading &&
              searchQuery.length >= 2 &&
              (!aircraftTypes || aircraftTypes.length === 0) && (
                <CommandEmpty>No aircraft types found for "{searchQuery}"</CommandEmpty>
              )}

            {!isLoading && searchQuery.length < 2 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Type at least 2 characters to search
              </div>
            )}

            {!isLoading && aircraftTypes && aircraftTypes.length > 0 && (
              <CommandGroup heading="Aircraft Types">
                {aircraftTypes.map((aircraftType) => (
                  <CommandItem
                    key={aircraftType.id}
                    value={aircraftType.id}
                    onSelect={() => handleSelect(aircraftType)}
                    className="flex items-center justify-between gap-2 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Plane className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium truncate">
                          {aircraftType.manufacturer} {aircraftType.model}
                          {aircraftType.variant && (
                            <span className="text-muted-foreground ml-1">
                              {aircraftType.variant}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {aircraftType.icao_designator && `ICAO: ${aircraftType.icao_designator} · `}
                          {aircraftType.wingspan}' wingspan · {aircraftType.length}' length
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {aircraftType.category && (
                        <Badge variant="secondary" className="text-xs">
                          {getCategoryLabel(aircraftType.category)}
                        </Badge>
                      )}
                      {value === aircraftType.id && <Check className="h-4 w-4" />}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
